import { useEffect, useMemo, useState } from "react";
import { PressureChart } from "@/components/PressureChart";
import { PressureFilters } from "@/components/PressureFilters";
import { ConditionsCalculator, PressureSummary, PressureTable, SectionTitle, analyzeRows } from "@/components/PressureInsights";
import { ConditionsImpact, FACTORS } from "@/components/ConditionsImpact";
import { PageHeader } from "@/pages/Events";
import { useSettings } from "@/context/SettingsContext";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { TYRES } from "@/lib/format";
import { findTarget, fromBar, stintsOf, toBar, tyreLabel } from "@/lib/pressureTargets";

const r = (v, unit) => Number(fromBar(v, unit).toFixed(unit === "psi" ? 1 : 3));

function buildSeries(stints, tyres, unit, kind, xKey = "track_temp") {
  return tyres.map((tyre) => ({
    tyre,
    points: stints.map((st) => {
      const hot = toBar(st.tyres?.[tyre]?.hot, st.unit), cold = toBar(st.tyres?.[tyre]?.cold, st.unit);
      const y = kind === "gain" ? (hot != null && cold != null ? hot - cold : null) : hot;
      if (st[xKey] == null || y == null) return null;
      const s = st.session;
      return { x: Number(st[xKey]), y: r(y, unit), tyre, session_name: `${s.name}${st.n === 2 ? " · S2" : ""}`, event_name: s.event_name, event_date: s.event_date,
        driver: s.driver, category: s.category, weather: s.conditions?.weather, air_temp: s.conditions?.air_temp, track_temp: st.track_temp, humidity: st.humidity, wind_speed: st.wind_speed };
    }).filter(Boolean),
  }));
}

export default function Pressures() {
  const { t } = useT();
  const targets = useSettings().settings.tyre_targets;
  const [tracks, setTracks] = useState(null);
  const [meta, setMeta] = useState({ drivers: [], categories: [], tyres: [] });
  const [f, setF] = useState({ track: "", events: [], days: [], driver: "", category: "", tyre: "", model: "", unit: "bar", trend: true });
  const [raw, setRaw] = useState([]);
  const [xKey, setXKey] = useState("track_temp");

  useEffect(() => {
    api.get("/events/tracks").then((res) => {
      setTracks(res.data);
      if (res.data.length) setF((p) => ({ ...p, track: p.track || res.data[0] }));
    });
    api.get("/events/meta").then((res) => setMeta(res.data));
  }, []);

  useEffect(() => {
    if (!f.track) {
      return;
    }
    const params = { track: f.track, ...(f.driver && { driver: f.driver }), ...(f.category && { category: f.category }) };
    api.get("/analysis/pressures", { params }).then((res) => {
      setRaw(res.data);
      const latest = [...res.data].sort((a, b) => (b.event_date || "").localeCompare(a.event_date || ""))[0];
      if (latest) setF((p) => (p.events.length ? p : { ...p, events: [latest.event_id] }));
    });
  }, [f.track, f.driver, f.category]);

  const eventOpts = useMemo(() => {
    const m = new Map();
    raw.forEach((s) => m.set(s.event_id, { label: `${(s.event_date || "").split("-").reverse().join("/")} · ${s.event_name}`, date: s.event_date || "", n: (m.get(s.event_id)?.n || 0) + 1 }));
    return [...m.entries()].sort((a, b) => b[1].date.localeCompare(a[1].date)).map(([id, v]) => [id, v.label, v.n]);
  }, [raw]);
  const inEvents = raw.filter((s) => f.events.includes(s.event_id));
  const dayOpts = [...new Set(inEvents.map((s) => s.session_date).filter(Boolean))].sort()
    .map((d) => [d, d.split("-").reverse().join("/"), inEvents.filter((s) => s.session_date === d).length]);

  const stints = useMemo(() => {
    const all = raw.filter((s) => f.events.includes(s.event_id) && (!f.days.length || f.days.includes(s.session_date))).flatMap(stintsOf);
    if (!f.model) return all;
    const [b, m] = f.model.split("|");
    return all.filter((st) => tyreLabel(st.brand, st.model).toLowerCase() === tyreLabel(b, m).toLowerCase());
  }, [raw, f.model, f.events, f.days]);
  const plotted = stints.filter((st) => st.track_temp != null && TYRES.some((p) => st.tyres?.[p]?.hot != null));
  const withHot = stints.filter((st) => TYRES.some((p) => st.tyres?.[p]?.hot != null));
  const xf = FACTORS.find((x) => x.key === xKey);
  const xAxis = { key: xKey, label: t(xf.label), unit: xf.unit };
  const excluded = stints.length - plotted.length;
  const target = useMemo(() => {
    if (f.model) return findTarget(targets, ...f.model.split("|"));
    const found = [...new Set(plotted.map((st) => findTarget(targets, st.brand, st.model)).filter(Boolean))];
    return found.length === 1 ? found[0] : null;
  }, [f.model, targets, plotted]);
  const rows = useMemo(() => analyzeRows(plotted, (st) => findTarget(targets, st.brand, st.model)), [plotted, targets]);
  const wheels = f.tyre ? [f.tyre] : TYRES;
  const band = target ? { lo: r(target.target - target.tolerance, f.unit), hi: r(target.target + target.tolerance, f.unit), mid: r(target.target, f.unit) } : null;

  return (
    <div>
      <PageHeader title={t("pressures_title")} sub={t("pressures_sub")} />
      {tracks && tracks.length === 0 ? (
        <div data-testid="pressure-no-tracks" className="border border-dashed border-[#282D3D] rounded-sm p-12 text-center text-slate-500 font-mono text-sm">{t("no_tracks")}</div>
      ) : (
        <>
          <PressureFilters f={f} setF={setF} tracks={tracks || []} meta={meta} eventOpts={eventOpts} dayOpts={dayOpts} />
          {!f.events.length ? (
            <div data-testid="pressure-need-event" className="border border-dashed border-[#FFB800]/50 rounded-sm p-12 text-center text-[#FFB800] font-mono text-sm">{t("need_event")}</div>
          ) : withHot.length === 0 ? (
            <div data-testid="pressure-no-points" className="border border-dashed border-[#282D3D] rounded-sm p-12 text-center text-slate-500 font-mono text-sm">{t("no_points")}</div>
          ) : (
            <div className="space-y-8">
              <section>
                <SectionTitle>{t("sec_summary")}</SectionTitle>
                <PressureSummary rows={rows} unit={f.unit} target={target} />
                {!f.model && !target && <p data-testid="pressure-pick-model-note" className="mt-2 font-mono text-xs text-[#FFB800]">{t("pick_model_note")}</p>}
              </section>
              <ConditionsCalculator key={`${f.track}-${f.model}-${f.driver}-${f.category}-${f.events.join()}-${f.days.join()}`} stints={plotted} unit={f.unit} target={target} />
              <ConditionsImpact stints={withHot} unit={f.unit} xKey={xKey} setXKey={setXKey} />
              <section>
                <SectionTitle right={
                  <div data-testid="pressure-x-axis" className="flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-[10px] uppercase text-slate-500 mr-1">{t("x_axis")}</span>
                    {FACTORS.map((x) => (
                      <button key={x.key} type="button" data-testid={`pressure-x-${x.key}`} onClick={() => setXKey(x.key)}
                        className={`px-2.5 py-1 rounded-sm border font-mono text-[11px] uppercase transition-colors duration-200 ${xKey === x.key ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-[#282D3D] text-slate-400 hover:text-white"}`}>{t(x.label)}</button>
                    ))}
                  </div>
                }>{t("sec_charts")}</SectionTitle>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <PressureChart series={buildSeries(withHot, wheels, f.unit, "hot", xKey)} unit={f.unit} trend={f.trend} band={band} title={t("hot_vs_target")} xAxis={xAxis} />
                  <PressureChart testid="gain-chart" series={buildSeries(withHot, wheels, f.unit, "gain", xKey)} unit={f.unit} trend={f.trend} yLabel={t("pa_gain")} title={t("gain_chart").replace("{x}", xAxis.label.toLowerCase())} xAxis={xAxis} />
                </div>
              </section>
              <section>
                <SectionTitle>{t("sec_sessions")}</SectionTitle>
                {excluded > 0 && <p data-testid="pressure-excluded-note" className="mb-2 font-mono text-xs text-slate-500">{excluded} {t("excluded_note")}</p>}
                <PressureTable rows={rows} unit={f.unit} />
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}
